/**
 * TikTok Pixel Tracking Service for Web
 * 
 * Platform-aware service that:
 * - Only runs on web (checks for window.ttq)
 * - Waits for the pixel to load before tracking
 * - Provides methods for CompleteRegistration and Purchase events
 * - Matches TikTok Pixel API event format exactly
 */

/**
 * Hash a string using SHA-256
 * @param {string} str - String to hash
 * @returns {Promise<string>} - Hashed string in hex format
 */
const hashSHA256 = async (str) => {
  if (!str) return null;
  
  try {
    // Convert string to ArrayBuffer
    const encoder = new TextEncoder();
    const data = encoder.encode(str.toLowerCase().trim());
    
    // Hash using Web Crypto API
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    
    // Convert ArrayBuffer to hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    
    return hashHex;
  } catch (error) {
    console.error('[TikTok Pixel] Error hashing string:', error);
    return null;
  }
};

/**
 * Identify user with PII data (hashed)
 * Call this before tracking events when user data is available
 * @param {Object} userData - User identification data
 * @param {string} [userData.email] - User email (will be hashed)
 * @param {string} [userData.externalId] - External ID like Firebase UID (will be hashed)
 * @param {string} [userData.phoneNumber] - Phone number (will be hashed)
 */
const identifyUser = async (userData = {}) => {
  try {
    const isAvailable = await checkTtqAvailable();
    if (!isAvailable) return;
    
    const identifyParams = {};
    
    // Hash and add email if available
    if (userData.email) {
      const hashedEmail = await hashSHA256(userData.email);
      if (hashedEmail) identifyParams.email = hashedEmail;
    }
    
    // Hash and add external ID (Firebase UID) if available
    if (userData.externalId) {
      const hashedExternalId = await hashSHA256(userData.externalId);
      if (hashedExternalId) identifyParams.external_id = hashedExternalId;
    }
    
    // Hash and add phone number if available
    if (userData.phoneNumber) {
      const hashedPhone = await hashSHA256(userData.phoneNumber);
      if (hashedPhone) identifyParams.phone_number = hashedPhone;
    }
    
    // Only call identify if we have at least one parameter
    if (Object.keys(identifyParams).length > 0) {
      window.ttq.identify(identifyParams);
      console.log(
        '%c[TikTok Pixel] 👤 User Identified',
        'background: #2196F3; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;',
        '\n📦 Parameters:', identifyParams
      );
    }
  } catch (error) {
    console.error('[TikTok Pixel] Error identifying user:', error);
  }
};

/**
 * Check if TikTok Pixel (ttq) is available
 * @returns {Promise<boolean>}
 */
const checkTtqAvailable = () => {
  if (typeof window === 'undefined') {
    console.log(
      '%c[TikTok Pixel] ⚠️ Window not available',
      'background: #ff9800; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;'
    );
    return Promise.resolve(false);
  }
  
  // If ttq is already available, return immediately
  if (typeof window.ttq === 'object' && window.ttq !== null) {
    console.log(
      '%c[TikTok Pixel] ✅ Pixel Loaded (ttq available)',
      'background: #2196F3; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;'
    );
    return Promise.resolve(true);
  }
  
  // Wait for ttq to load (max 5 seconds)
  console.log(
    '%c[TikTok Pixel] ⏳ Waiting for pixel to load...',
    'background: #ff9800; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;'
  );
  
  return new Promise((resolve) => {
    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      if (typeof window.ttq === 'object' && window.ttq !== null) {
        clearInterval(checkInterval);
        console.log(
          '%c[TikTok Pixel] ✅ Pixel Loaded Successfully',
          'background: #4CAF50; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;',
          `\n⏱️ Load time: ${Date.now() - startTime}ms`
        );
        resolve(true);
      } else if (Date.now() - startTime > 5000) {
        clearInterval(checkInterval);
        console.error(
          '%c[TikTok Pixel] ❌ Pixel Load Timeout (5s)',
          'background: #f44336; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;'
        );
        resolve(false);
      }
    }, 100);
  });
};

/**
 * Track CompleteRegistration event
 * This event is fired when a new user creates an account
 * Matches TikTok Pixel API format exactly
 * 
 * @param {Object} registrationData - Registration information
 * @param {string} registrationData.method - Registration method: 'email', 'google', or 'apple'
 * @param {string} [registrationData.email] - User email (will be hashed for identify)
 * @param {string} [registrationData.externalId] - Firebase UID (will be hashed for identify)
 * @param {number} [registrationData.value] - Optional value for registration
 * @param {string} [registrationData.currency] - Currency code (default: 'USD')
 */
export const trackCompleteRegistration = async (registrationData = {}) => {
  try {
    // Handle both old format (string method) and new format (object)
    const method = typeof registrationData === 'string' ? registrationData : registrationData.method || 'email';
    const email = typeof registrationData === 'object' ? registrationData.email : null;
    const externalId = typeof registrationData === 'object' ? registrationData.externalId : null;
    const value = typeof registrationData === 'object' ? registrationData.value : undefined;
    const currency = typeof registrationData === 'object' ? registrationData.currency || 'USD' : 'USD';
    
    console.log(
      '%c[TikTok Pixel] 🔄 Attempting to track CompleteRegistration...',
      'background: #2196F3; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;',
      `\n📝 Method: ${method.toUpperCase()}`
    );
    
    const isAvailable = await checkTtqAvailable();
    if (!isAvailable) {
      console.error(
        '%c[TikTok Pixel] ❌ CompleteRegistration Event NOT Fired - Pixel not available',
        'background: #f44336; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;'
      );
      return;
    }
    
    // Identify user with PII data before tracking event
    if (email || externalId) {
      await identifyUser({ email, externalId });
    }
    
    // Generate unique event ID for deduplication
    const eventId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // TikTok Pixel event format - using contents array
    // Value is required by TikTok - use 1 for registrations (represents one registration)
    // TikTok may not accept 0 as a valid value, so we use 1 to represent one registration event
    // Ensure value is a plain number (integer or decimal) - no formatting, no strings
    let numericValue = 1; // Default to 1 (one registration) instead of 0
    if (value !== undefined && value !== null) {
      const converted = Number(value);
      numericValue = isNaN(converted) ? 1 : converted;
      // Ensure it's at least 1 (TikTok may require non-zero values)
      if (numericValue <= 0) {
        numericValue = 1;
      }
    }
    // Use integer 1 for registrations (not 1.0 or 1.00)
    const finalValue = numericValue === 1 ? 1 : numericValue;
    
    const eventParams = {
      contents: [
        {
          content_id: 'account_registration',
          content_type: 'product',
          content_name: `Account Registration - ${method}`
        }
      ],
      value: finalValue, // Plain number: integer (0) or decimal - no formatting
      currency
    };
    
    // TikTok Pixel track event with event_id in third parameter
    window.ttq.track('CompleteRegistration', eventParams, {
      event_id: eventId
    });
    
    console.log(
      '%c[TikTok Pixel] ✅✅✅ CompleteRegistration Event FIRED! ✅✅✅',
      'background: #4CAF50; color: white; padding: 8px 12px; border-radius: 4px; font-weight: bold; font-size: 14px;',
      '\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
      `\n🎉 EVENT: CompleteRegistration`,
      `\n📝 Method: ${method.toUpperCase()}`,
      `\n🆔 Event ID: ${eventId}`,
      `\n📦 Parameters:`,
      eventParams,
      `\n⏰ Timestamp: ${new Date().toISOString()}`,
      `\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`
    );
  } catch (error) {
    console.error(
      '%c[TikTok Pixel] ❌ Error tracking CompleteRegistration',
      'background: #f44336; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;',
      error
    );
  }
};

/**
 * Track Purchase event
 * This event is fired when a user completes a purchase through RevenueCat
 * Matches TikTok Pixel API format exactly
 * 
 * @param {Object} purchaseData - Purchase information
 * @param {number} purchaseData.amount - Purchase amount (in dollars, not cents)
 * @param {string} purchaseData.currency - Currency code (e.g., 'USD')
 * @param {string} purchaseData.productId - Product identifier
 * @param {string} purchaseData.transactionId - Transaction identifier (for deduplication)
 * @param {string} [purchaseData.contentName] - Product name/description
 * @param {string} [purchaseData.contentCategory] - Product category (default: 'subscription')
 * @param {string} [purchaseData.email] - User email (will be hashed for identify)
 * @param {string} [purchaseData.externalId] - Firebase UID (will be hashed for identify)
 */
export const trackPurchase = async (purchaseData) => {
  try {
    console.log(
      '%c[TikTok Pixel] 🔄 Attempting to track Purchase...',
      'background: #2196F3; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;',
      '\n📦 Purchase Data:',
      purchaseData
    );
    
    const isAvailable = await checkTtqAvailable();
    if (!isAvailable) {
      console.error(
        '%c[TikTok Pixel] ❌ Purchase Event NOT Fired - Pixel not available',
        'background: #f44336; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;'
      );
      return;
    }
    
    const {
      amount,
      currency,
      productId,
      transactionId,
      contentName,
      contentCategory = 'subscription',
      email,
      externalId
    } = purchaseData;
    
    // Identify user with PII data before tracking event
    if (email || externalId) {
      await identifyUser({ email, externalId });
    }
    
    // Generate unique event ID for deduplication (use transactionId if available)
    const eventId = transactionId || `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Ensure amount is a plain number (integer or decimal) - no formatting, no strings
    let numericAmount = 0;
    if (amount !== undefined && amount !== null) {
      const converted = Number(amount);
      numericAmount = isNaN(converted) ? 0 : converted;
    }
    
    // TikTok Pixel event format - using contents array
    const eventParams = {
      contents: [
        {
          content_id: productId,
          content_type: 'product',
          content_name: contentName || productId
        }
      ],
      value: numericAmount, // Plain number: integer or decimal - no formatting
      currency: currency || 'USD'
    };
    
    // TikTok Pixel track event with event_id in third parameter
    window.ttq.track('Purchase', eventParams, {
      event_id: eventId
    });
    
    console.log(
      '%c[TikTok Pixel] ✅✅✅ Purchase Event FIRED! ✅✅✅',
      'background: #4CAF50; color: white; padding: 8px 12px; border-radius: 4px; font-weight: bold; font-size: 14px;',
      '\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
      `\n💰 EVENT: Purchase`,
      `\n💵 Amount: ${currency || 'USD'} ${amount.toFixed(2)}`,
      `\n📦 Product ID: ${productId}`,
      `\n🆔 Transaction ID: ${transactionId || 'N/A'}`,
      `\n🆔 Event ID: ${eventId}`,
      `\n📝 Product Name: ${contentName || productId}`,
      `\n📂 Category: ${contentCategory}`,
      `\n📦 Parameters:`,
      eventParams,
      `\n⏰ Timestamp: ${new Date().toISOString()}`,
      `\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`
    );
  } catch (error) {
    console.error(
      '%c[TikTok Pixel] ❌ Error tracking Purchase',
      'background: #f44336; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;',
      error
    );
  }
};

/**
 * Debug function to test TikTok Pixel availability
 * Can be called from browser console: window.testTikTokPixel()
 */
if (typeof window !== 'undefined') {
  window.testTikTokPixel = async () => {
    const isAvailable = await checkTtqAvailable();
    console.log('TikTok Pixel Available:', isAvailable);
    if (isAvailable) {
      console.log('TikTok Pixel Object:', window.ttq);
    }
    return isAvailable;
  };
}

